import api from './api';

/**
 * A card ID is opaque to the client.
 *
 * Pages used to validate against `/^[a-f0-9]{40}-\d+$/i`, which is the shape of
 * the old Shopify pool IDs only: it rejected in-person `AAAA` cards and would
 * reject newly generated IDs too. The server owns the meaning of an ID, so the
 * client checks only that something plausible was typed.
 */
export const CARD_ID_SHAPE = /^[A-Za-z0-9][A-Za-z0-9_:-]{5,127}$/;

export function looksLikeCardId(value) {
  return CARD_ID_SHAPE.test(String(value || '').trim());
}

/**
 * One way to activate a card.
 *
 * The app used to choose between two endpoints by testing the card ID for an
 * `AAAA` prefix — in-person cards went to /api/cards/activate-inperson and
 * everything else to /api/user/updateProfile, which performed no validation at
 * all. Card type is now a property of the card record on the server, so the
 * client no longer needs to know or guess it.
 */
export async function activateCard(cardId, activationProof) {
  const trimmed = String(cardId || '').trim();
  if (!trimmed) {
    throw new Error('Card ID is required');
  }
  return api.post('/api/cards/claim', {
    cardId: trimmed,
    ...(activationProof ? { activationProof } : {}),
  });
}

/**
 * Turn a failed activation into something worth showing a person. The server
 * sends a `code` alongside the message, so the copy can be specific about what
 * to do next rather than saying "try again" to someone who cannot.
 */
export function activationErrorMessage(error, fallback = 'Failed to activate card. Please try again.') {
  const data = error?.response?.data;
  if (data?.error) return data.error;

  switch (data?.code) {
    case 'CARD_NOT_FOUND':
      return 'We do not recognise that card ID. Please check it and try again.';
    case 'RESERVED_FOR_OTHER':
      return 'This card was supplied to a different email address. Sign in with that address, or contact support to move it to this account.';
    case 'ALREADY_CLAIMED':
      return 'This card has already been activated on another account.';
    case 'USER_ALREADY_HAS_CARD':
      return 'This card could not be added to your account. Contact support if you need help.';
    case 'REVOKED':
      return 'This card has been cancelled. Please contact support.';
    default:
      return fallback;
  }
}

/** True when the failure means this account already holds a different card. */
export function isAlreadyHasCardError(error) {
  const data = error?.response?.data;
  if (data?.code === 'USER_ALREADY_HAS_CARD') return true;
  return Boolean(data?.error && data.error.includes('already has a card linked'));
}
